import { Box, Card, Grid, Stack, debounce, styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ISubmissionSettingsResponse,
  ISubmissionSettingsTemplate,
  saveSubmissionTemplate,
  suggestTemplateNames,
} from 'wisestep-api';
import { Label, WsButton, WsTypography, WsTypoVariants, WsAutoComplete } from 'wisestep-base';

import { SettingPageBreadCrumbs } from '../../../components/SettingPageBreadCrumbs/SettingPageBreadCrumbs';
import SettingsIndex from '../../../components/SettingsIndex';
import { IBreadCrumbsList } from '../../../components/WsBreadCrumbs';
import { useWorkspaceContext } from '../../../context';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import ErrorLogger from '../../../providers/ErrorLogger';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { HeaderBox } from '../../jobSettings/components/HeaderBox';

import { ILocationState } from './submissionEmailTemplate';
import { defaultFormValues, submissionFieldsArrayType } from './submissionFormHelper';

const StyledLabel = styled(Label)(() => ({
  fontWeight: 'bold',
  width: '13vw',
  display: 'flex',
  justifyContent: 'space-between',
  paddingRight: '2vw',
}));
const backUrlsList = (): IBreadCrumbsList[] => [
  {
    label: 'Settings',
    url: '/app-settings/crm',
  },
  {
    label: 'Customize Submission Flow',
    url: '/crm-settings/configure-submission-flow',
  },
  {
    label: 'Add Template',
    url: `${window.location.pathname}${window.location.search}`,
  },
];

const ConfigSubmissionFormPageNew = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const workspaceContext = useWorkspaceContext();
  const location = useLocation();
  const state = (location.state as ILocationState) ?? null;
  const payload = state?.payload || null;
  const [submissionFormSettingsData, setSubmissionFormSettingsData] =
    useState<ISubmissionSettingsResponse>();
  const [dropdownList, setDropdownList] = useState<string[]>([]);
  const [templateOptions, setTemplateOptions] = useState<ISubmissionSettingsTemplate[]>([]);
  const formOptions = {
    defaultValues: defaultFormValues,
  };
  const methodsMain = useForm(formOptions);
  const { handleSubmit, setValue, control, reset } = methodsMain;

  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_SUBMISSION_FIELDS_CUSTOMISATION,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];
  const navigate = useNavigate();

  const fieldsArray = useMemo(
    () =>
      Object.entries(submissionFormSettingsData ?? defaultFormValues)
        .filter(([, value]) => value !== null)
        .map(([key]) => key as submissionFieldsArrayType),
    [submissionFormSettingsData]
  );

  useEffect(() => {
    if (payload?.fields) {
      setSubmissionFormSettingsData(payload.fields);
    } else {
      setSubmissionFormSettingsData(defaultFormValues);
    }
  }, [payload]);

  useEffect(() => {
    if (submissionFormSettingsData) {
      reset(submissionFormSettingsData);
    }
  }, [reset, submissionFormSettingsData]);

  const onSaveHandler = async (formData: FieldValues) => {
    if (!manageAccess) {
      setNotification(warning('You do not have enough permissions to perform this action'));
      return;
    }

    try {
      const req = { ...payload, fields: formData as ISubmissionSettingsResponse };
      await saveSubmissionTemplate(req as ISubmissionSettingsTemplate);
      setNotification(success('Submission template created or updated successfully'));
      onCancel();
    } catch (error) {
      setNotification(warning('Something went wrong. Please try again'));
    }
  };
  const onClickHandler = (data, boolValue, type) => {
    const tempData = data;
    const handler = {
      ['required']: () => {
        tempData.required = boolValue;
      },
      ['hidden']: () => {
        tempData.hidden = boolValue;
      },
    };
    handler[type]();
    return { ...tempData };
  };
  const onPrev = () =>
    navigate('/crm-settings/configure-submission-email-template', {
      state: {
        payload,
      },
    });

  const onCancel = () => navigate('/crm-settings/configure-submission-flow');

  const onSelect = (value: string[]) => {
    setValue('importTemplate', value[0]);
    const selectedValue = templateOptions?.find((item) => item?.templateName === value[0]);
    setSubmissionFormSettingsData(selectedValue?.fields);
  };

  const onSearch = debounce(async (templateName: string) => {
    if (!templateName.trim()) return [];
    const res = await suggestTemplateNames(templateName);
    const data = res?.data.map((item) => item?.templateName || '');
    setDropdownList(data);
    setTemplateOptions(res?.data || []);
  }, 300);

  const onSearchKey = (searchText: string): Promise<void> => {
    onSearch(searchText);
    return Promise.resolve();
  };

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <SettingPageBreadCrumbs breadCrumbsList={backUrlsList()}>
        <Grid container columnSpacing={3}>
          <Grid item xs={3}>
            <SettingsIndex
              index={''}
              options={[
                {
                  label: '01. Email Template',
                  url: '/crm-settings/configure-submission-email-template',
                },
                { label: '02. Submission Form', url: '/crm-settings/configure-submission-form' },
              ]}
              isNewSubmissionForm={true}
            />
          </Grid>
          <Grid item xs={9}>
            <Card sx={{ minHeight: '83vh', width: '100%', p: 2 }}>
              <Box component="form" px={8} pt={2}>
                <FormProvider {...methodsMain}>
                  <form>
                    <HeaderBox mb={4}>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                        Submission Fields
                      </WsTypography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          flex: 0.5,
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Import from</WsTypography>
                        <Grid sx={{ width: '100%' }}>
                          <WsAutoComplete
                            onValueEnter={onSearchKey}
                            onTagsChange={onSelect}
                            placeholder="Select Existing Template"
                            initialValue={[]}
                            optionsInStringArray={dropdownList}
                            multiple={false}
                            name="importTemplate"
                            size="small"
                          />
                        </Grid>
                      </Stack>
                    </HeaderBox>
                    <Grid container direction="column" rowSpacing={1} mb={2}>
                      {submissionFormSettingsData &&
                        fieldsArray?.map((fieldName: submissionFieldsArrayType) => (
                          <Grid
                            key={fieldName}
                            item
                            display="flex"
                            justifyContent={'space-between'}
                          >
                            <>
                              <Controller
                                name={fieldName}
                                control={control}
                                render={(props) => (
                                  <>
                                    <StyledLabel>{props.field.value?.label}</StyledLabel>
                                    <WsButton
                                      variant={
                                        props.field.value?.required ? 'contained' : 'outlined'
                                      }
                                      disabled={!manageAccess || props.field.value?.disabled}
                                      onClick={() => {
                                        setValue(
                                          fieldName,
                                          onClickHandler(props.field.value, true, 'required')
                                        );
                                        setValue(
                                          fieldName,
                                          onClickHandler(props.field.value, false, 'hidden')
                                        );
                                      }}
                                    >
                                      Mandatory
                                    </WsButton>
                                    <WsButton
                                      variant={
                                        props.field.value?.required ? 'outlined' : 'contained'
                                      }
                                      disabled={!manageAccess || props.field.value?.disabled}
                                      onClick={() =>
                                        setValue(
                                          fieldName,
                                          onClickHandler(props.field.value, false, 'required')
                                        )
                                      }
                                    >
                                      Non-Mandatory
                                    </WsButton>
                                  </>
                                )}
                              />
                              <Controller
                                name={fieldName}
                                control={control}
                                render={(props) => (
                                  <>
                                    <WsButton
                                      variant={props.field.value?.hidden ? 'outlined' : 'contained'}
                                      disabled={
                                        !manageAccess || props.field.value?.required
                                          ? true
                                          : props.field.value?.disabled ||
                                            props.field.value?.label === 'Email'
                                      }
                                      onClick={() =>
                                        setValue(
                                          fieldName,
                                          onClickHandler(props.field.value, false, 'hidden')
                                        )
                                      }
                                    >
                                      Show
                                    </WsButton>
                                    <WsButton
                                      variant={props.field.value?.hidden ? 'contained' : 'outlined'}
                                      disabled={
                                        !manageAccess || props.field.value?.required
                                          ? true
                                          : props.field.value?.disabled ||
                                            props.field.value?.label === 'Email'
                                      }
                                      onClick={() =>
                                        setValue(
                                          fieldName,
                                          onClickHandler(props.field.value, true, 'hidden')
                                        )
                                      }
                                    >
                                      Hide
                                    </WsButton>
                                  </>
                                )}
                              />
                            </>
                          </Grid>
                        ))}
                    </Grid>
                    <Grid>
                      <Stack direction="row" spacing={1} justifyContent="flex-end" pt={2} pr={2}>
                        <WsButton onClick={onCancel} size="medium" variant="outlined">
                          Cancel
                        </WsButton>
                        <WsButton onClick={onPrev} size="medium" variant="outlined">
                          Prev
                        </WsButton>
                        <WsButton size="medium" onClick={handleSubmit(onSaveHandler)}>
                          Save Template
                        </WsButton>
                      </Stack>
                    </Grid>
                  </form>
                </FormProvider>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </SettingPageBreadCrumbs>
    </ErrorBoundary>
  );
};

export default ConfigSubmissionFormPageNew;
